import {
  AfterViewInit,
  Component,
  ViewChild,
  computed,
  effect,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IonContent, IonSearchbar, ModalController } from '@ionic/angular';
import { EMPTY } from 'rxjs';
import { AttendeeInformation, CalendarEvent, Place } from 'src/app/interfaces/interfaces';
import { ApiService } from 'src/app/services/api-services/api.service';
import { AttendeesStore } from 'src/app/stores/attendees.store';
import { ColleaguesStore } from 'src/app/stores/colleagues.store';
import { PlacesStore } from 'src/app/stores/places.store';
import { FloorplanSearchService } from '../search/floorplan-search.service';
import * as path from 'path';
export interface ColleagueCalendarEvents {
  colleagueUpn: string;
  colleagueInfo: ColleagueInfoToShow[];
}
export interface ColleagueInfoToShow {
  displayName: string;
  userAvatarUrl: string;
  location: string;
  jobTitle: string;
  floorMapId: string;
  place: Place;
}
@Component({
  selector: 'app-find-colleague',
  templateUrl: './find-colleague.component.html',
  styleUrls: ['./find-colleague.component.scss'],
})
export class FindColleagueComponent implements OnInit, OnDestroy, AfterViewInit {
  private fetchedUpns = new Set<string>();
  @ViewChild('colleagueSearchBar') colleagueSearchBar: IonSearchbar;
  noColleagueFound: boolean = false;
  constructor(
    private modalController: ModalController,
    private attendeesStore: AttendeesStore,
    private placesStore: PlacesStore,
    private colleaguesStore: ColleaguesStore,
    private floorplanSearchService: FloorplanSearchService
  ) {
    effect(
      () => {
        const attendees = this.attendeesList();
        if (!attendees || attendees.length === 0) {
          return;
        }

        const upnsToFetch = attendees
          .map((att) => att.userPrincipalName.toLowerCase())
          .filter((upn) => !this.fetchedUpns.has(upn));

        if (upnsToFetch.length === 0) return;

        upnsToFetch.forEach((upn) => this.fetchedUpns.add(upn));

        this.colleaguesStore.fetchColleagues(upnsToFetch);
      },
      { allowSignalWrites: true }
    );
  }
  searchTerm: WritableSignal<string> = signal('');
  foundedCollegues: WritableSignal<AttendeeInformation[]> = signal([]);
  attendeesList: Signal<AttendeeInformation[]> = computed(() => {
    if (this.searchTerm().length < 3) return null;

    return this.attendeesStore
      .attendees()
      .filter((attendee) =>
        this.attendeesStore.findAttendee(attendee, this.searchTerm()) 
      && attendee.allowColleagueFinding
  )
  }
  );
  isFetchingColleagues = computed(() => this.colleaguesStore.loading());
  calendarEventsByColleague = computed<ColleagueCalendarEvents[]>(() => {
    const colleagues = this.colleaguesStore.colleagues();
    if (!colleagues || colleagues.length === 0) {
      this.noColleagueFound = true;
      return [];
    }



    const visibleUpns = new Set(
      (this.attendeesList() ?? []).map((att) =>
        att.userPrincipalName.toLowerCase()
      )
    );
    if (!visibleUpns || visibleUpns.size === 0) {
      this.noColleagueFound = true;
    }
    else {
      this.noColleagueFound = false;
    }

    return (
      colleagues
        // hide non-matching colleagues
        .filter((colleague) =>
          visibleUpns.has(colleague.colleagueUpn.toLowerCase())
        )
        .map((colleague) => {
          let events: CalendarEvent[] = [];
  
          const hasLocations =
            Array.isArray(colleague.locations) && colleague.locations.length > 0;
  
          if (hasLocations) {
            events = colleague.locations.filter(e => e?.place?.emailAddress);
      }
      else if (colleague.parking) {
            events = [colleague.parking];
          }

          const colleagueInfo = this.attendeesStore
            .attendees()
            .find(
              (att) =>
                att.userPrincipalName.toLowerCase() ===
                colleague.colleagueUpn.toLowerCase()
            );


          const colleagueFullName =
            colleagueInfo?.firstName && colleagueInfo?.lastName
              ? `${colleagueInfo.firstName} ${colleagueInfo.lastName}`
              : colleague.colleagueUpn;

          const colleagueInfoToShow = [];

          if (!events || events.length === 0) {
            colleagueInfoToShow.push({
              displayName: colleagueFullName,
              location: 'colleagues.colleagueFree',
              jobTitle: colleagueInfo?.jobTitle || '',
              floorMapId: '',
            });
          }
          events.forEach((event) => {
            colleagueInfoToShow.push({
              displayName: colleagueFullName,
              location: this.generateOfficerLocation(event.place as Place),
              jobTitle: colleagueInfo?.jobTitle || '',
              floorMapId: event.place?.floorMapId || '',
              place: event.place,
            });
          });
          colleagueInfoToShow?.forEach(info => {
              info.userAvatarUrl = this.attendeesStore.findAttendeeAvatar(colleague.colleagueUpn);
          })
          return {
            colleagueUpn: colleague.colleagueUpn,
            colleagueInfo: colleagueInfoToShow,
          };
        })
    );
  });

  ngOnInit() {}
  ngAfterViewInit(): void {
    // Run after modal content is mounted to reliably focus search input.
    setTimeout(() => {
      this.colleagueSearchBar?.setFocus();
    }, 100);
  }
  ngOnDestroy() {
    this.resetState();
  }
  onDone() {

    this.modalController.dismiss();
  }
  dismissModal() {
    this.modalController.dismiss();
  }
  filterAttendees(event) {
    this.searchTerm.set(event.target.value);
  }
  FilterByColleague(colleagueUpn: string) {
    this.searchTerm.set(colleagueUpn);
  }
  private generateOfficerLocation(place: Place): string {
    if (!place) return 'colleagues.colleagueFree';
    let addressString =  this.buildAddressString(place);
    if (addressString != null && addressString.length > 0) {
      addressString = `, ${this.buildAddressString(place)}`
    }
    return `${place.displayName}${addressString}`;
  }
  private buildAddressString(place: Place): string {
    if (!place) return '';
    const parts = [];
    if (place.address?.street) parts.push(place.address.street);
    if (place.address?.city) parts.push(place.address.city);
    if (place.address?.country) parts.push(place.address.country);
    return parts.join(', ');
  }
  openMap(colleagueInfo: ColleagueInfoToShow) {
    this.floorplanSearchService.openMap(colleagueInfo.place);
  }
  private resetState(): void {
    // Component state
    this.searchTerm.set('');
    this.fetchedUpns.clear();

    // Store state
    this.colleaguesStore.reset();
  }
}

